// Week ledger data — the per-day view model the LOG surfaces render (7
// columns on desktop, one day + a week strip on mobile).
//
// Reads use the SAME query keys as `useDayData(date)` (`['daily', date]`,
// `['workouts', date]`, `['meal-items', date, slot]`, `['weights']`) so the
// inspectors' optimistic mutations land in the grid instantly and every
// existing invalidation already covers the ledger. Seven tiny per-date reads
// beat a new range endpoint here: one cache, one truth.

import { useQueries, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { z } from 'zod';
import { apiClient } from './api';
import { useCardioSessions } from './cardio-queries';
import {
  buildMealPlan,
  deriveTodayPlanAndWorkout,
  filterFromStart,
  isoToday,
  resolveEffectiveTarget,
  resolveMealLog,
  useCoachLatest,
  useDailyRunTargets,
  useDailyTargetHistory,
  useProfile,
  type EffectiveTarget,
  type MealAnchor,
} from './day-data';
import { type MealItem, type MealItemsResponse } from './dish-data';
import {
  FOOD_SLOTS,
  isRestType,
  isRunType,
  mealCellState,
  weekDates,
  type FoodSlot,
  type LedgerRow,
  type MealCellState,
} from './ledger';
import { STALE_TIME_DAILY } from './query';
import type { WeekDay } from './protocol';
import {
  DEFAULT_PROFILE,
  DailyRowsSchema,
  WeightRowsSchema,
  WorkoutRowsSchema,
  type CardioSessionSummary,
  type DailyRow,
  type DailyRunTarget,
  type MealLog,
  type ProfileResponse,
  type WeightRow,
  type WorkoutRow,
} from './types';

/** Polls the wall clock every minute → minutes since local midnight. Drives
 *  the "due" / "missing" meal states for today. */
function useCurrentMinutes(): number {
  const read = () => {
    const now = new Date();
    return now.getHours() * 60 + now.getMinutes();
  };
  const [minutes, setMinutes] = useState<number>(read);
  useEffect(() => {
    const id = window.setInterval(() => setMinutes(read()), 60 * 1000);
    return () => window.clearInterval(id);
  }, []);
  return minutes;
}

const MealItemsSchema = z.object({
  date: z.string(),
  slot: z.enum(['lunch', 'snack', 'dinner']),
  items: z.array(
    z.object({
      id: z.number(),
      date: z.string(),
      slot: z.enum(['lunch', 'snack', 'dinner']),
      position: z.number(),
      dish_id: z.number(),
      dish_name: z.string(),
      grams: z.number(),
      cal: z.number(),
      p: z.number(),
      f: z.number(),
      c: z.number(),
      created_at: z.number(),
    }),
  ),
  totals: z.object({ cal: z.number(), p: z.number(), f: z.number(), c: z.number() }),
});

const RotationSelectionsSchema = z.array(
  z.object({
    date: z.string(),
    slot: z.enum(['lunch', 'snack', 'dinner']),
    dish_id: z.number(),
    dish_name: z.string(),
    updated_at: z.number(),
  }),
);
export type PlannedDish = z.infer<typeof RotationSelectionsSchema>[number];

export type LedgerMeal = {
  slot: FoodSlot;
  anchor: MealAnchor;
  /** Logged macros for the slot (from `meals_eaten`), null when unlogged. */
  log: MealLog | null;
  /** Dish items backing the log; `undefined` while not yet fetched. */
  items: MealItem[] | undefined;
  /** The household rotation's planned dish for this date+slot. */
  planned: PlannedDish | null;
  state: MealCellState;
};

export type LedgerSession = {
  plan: WeekDay | undefined;
  workout: WorkoutRow | undefined;
  type: string | undefined;
  /** Who wrote the active row: coach APPLY or the operator. Undefined when
   *  the day has no row (schedule-only). */
  source: WorkoutRow['source'] | undefined;
  /** Coach-sourced rows show the prescription's free-form name; everything
   *  else the plan label. */
  label: string;
  done: boolean;
  rest: boolean;
  /** Past day, real session, not completed → a gap. */
  missing: boolean;
  /** The .fit session recorded on this date (run or padel), if any. */
  fit: CardioSessionSummary | null;
  /** Coach reasoning for the day's activity (the "◆ note"). */
  coachNote: string | null;
  runTarget: DailyRunTarget | null;
};

export type LedgerDay = {
  date: string;
  isToday: boolean;
  isPast: boolean;
  isFuture: boolean;
  weight: number | null;
  /** Most recent weight strictly before `date` (any gap). */
  weightPrev: number | null;
  daily: DailyRow | undefined;
  target: EffectiveTarget;
  mealPlan: MealAnchor[];
  meals: Record<FoodSlot, LedgerMeal>;
  /** Day totals from the daily row (server-summed). */
  totals: { cal: number; p: number; f: number; c: number };
  mealsLogged: number;
  session: LedgerSession;
  /** Coach note for the day (session reason / run description). */
  coachNote: string | null;
  gaps: LedgerRow[];
};

export type Gap = { date: string; row: Exclude<LedgerRow, 'day'> };

export type LedgerWeek = {
  weekStart: string;
  dates: string[];
  days: LedgerDay[];
  byDate: Map<string, LedgerDay>;
  gaps: Gap[];
  profile: ProfileResponse;
  weights: WeightRow[];
  today: string;
  nowMin: number;
  loading: boolean;
  error: Error | null;
};

/** Rotation selections for a date range, keyed `${date}:${slot}`. */
export function useRotationSelections(from: string, to: string) {
  return useQuery({
    queryKey: ['rotation-selections', from, to],
    queryFn: async () => {
      const rows = await apiClient.get(`/api/rotation/selections?from=${from}&to=${to}`, {
        schema: RotationSelectionsSchema,
      });
      return new Map(rows.map((r) => [`${r.date}:${r.slot}`, r]));
    },
    staleTime: STALE_TIME_DAILY,
  });
}

/**
 * The week's ledger. `mealItemDates` lists the dates whose dish items should
 * be fetched (desktop: all seven; mobile: the selected day only).
 */
export function useLedgerWeek(weekStart: string, mealItemDates: readonly string[]): LedgerWeek {
  const today = isoToday();
  const nowMin = useCurrentMinutes();
  const dates = useMemo(() => weekDates(weekStart), [weekStart]);
  const weekEnd = dates[6]!;

  const profileQuery = useProfile();
  const profile = profileQuery.data;
  const targetHistory = useDailyTargetHistory();
  const runTargets = useDailyRunTargets();
  const coachLatest = useCoachLatest();

  const weightsQuery = useQuery<WeightRow[]>({
    queryKey: ['weights'],
    queryFn: () => apiClient.get<WeightRow[]>('/api/weights', { schema: WeightRowsSchema }),
    staleTime: STALE_TIME_DAILY,
  });

  const dailyQueries = useQueries({
    queries: dates.map((date) => ({
      queryKey: ['daily', date],
      queryFn: () =>
        apiClient.get<DailyRow[]>(`/api/daily?from=${date}&to=${date}`, {
          schema: DailyRowsSchema,
        }),
      staleTime: STALE_TIME_DAILY,
    })),
  });
  const workoutQueries = useQueries({
    queries: dates.map((date) => ({
      queryKey: ['workouts', date],
      queryFn: () =>
        apiClient.get<WorkoutRow[]>(`/api/workouts?from=${date}&to=${date}`, {
          schema: WorkoutRowsSchema,
        }),
      staleTime: STALE_TIME_DAILY,
    })),
  });

  const itemKeys = useMemo(
    () =>
      mealItemDates
        .filter((d) => dates.includes(d))
        .flatMap((date) => FOOD_SLOTS.map((slot) => ({ date, slot }))),
    [mealItemDates, dates],
  );
  const itemQueries = useQueries({
    queries: itemKeys.map(({ date, slot }) => ({
      queryKey: ['meal-items', date, slot],
      queryFn: () =>
        apiClient.get<MealItemsResponse>(`/api/daily/${date}/meals/${slot}/items`, {
          schema: MealItemsSchema,
        }),
      staleTime: STALE_TIME_DAILY,
    })),
  });

  const selectionsQuery = useRotationSelections(weekStart, weekEnd);

  // .fit sessions — the server window defaults to 90 days; older weeks need
  // an explicit `since` so their sessions still resolve.
  const cardioOpts = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 85);
    const cutoffYmd = cutoff.toISOString().slice(0, 10);
    return weekStart < cutoffYmd ? { since: `${weekStart}T00:00:00Z`, limit: 50 } : { limit: 200 };
  }, [weekStart]);
  const runsQuery = useCardioSessions('run', cardioOpts);
  const padelQuery = useCardioSessions('padel', cardioOpts);

  const dailyData = dailyQueries.map((q) => q.data);
  const workoutData = workoutQueries.map((q) => q.data);
  const itemData = itemQueries.map((q) => q.data);
  const dailyLoading = dailyQueries.some((q) => q.isLoading);
  const workoutLoading = workoutQueries.some((q) => q.isLoading);
  const firstError =
    profileQuery.error ??
    weightsQuery.error ??
    dailyQueries.find((q) => q.error)?.error ??
    workoutQueries.find((q) => q.error)?.error ??
    itemQueries.find((q) => q.error)?.error ??
    selectionsQuery.error ??
    runsQuery.error ??
    padelQuery.error ??
    null;

  // Computed every render: seven days of small derivations is cheaper than a
  // memo whose dependency list would have to spread variable-length arrays.
  const week = ((): Omit<LedgerWeek, 'loading' | 'error' | 'nowMin' | 'today'> => {
    const prof = profile ?? DEFAULT_PROFILE;
    const startDate = prof.start_date ?? null;
    const sortedWeights = filterFromStart(
      [...(weightsQuery.data ?? [])].sort((a, b) => a.date.localeCompare(b.date)),
      startDate,
    );
    const itemsByKey = new Map<string, MealItem[]>();
    itemKeys.forEach((k, i) => {
      const data = itemData[i];
      if (data) itemsByKey.set(`${k.date}:${k.slot}`, data.items);
    });
    const fitByDate = new Map<string, CardioSessionSummary>();
    for (const s of [...(runsQuery.data ?? []), ...(padelQuery.data ?? [])]) {
      const key = s.workout_log_match?.date ?? s.recorded_date;
      if (!fitByDate.has(key)) fitByDate.set(key, s);
    }
    const selections = selectionsQuery.data ?? new Map<string, PlannedDish>();
    const suggestions = coachLatest.data?.suggestions ?? [];

    const days: LedgerDay[] = dates.map((date, i) => {
      const daily = (dailyData[i] ?? []).find((r) => r.date === date);
      const workouts = workoutData[i] ?? [];
      const target = resolveEffectiveTarget(targetHistory, date);
      const mealPlan = buildMealPlan(target);
      const isToday = date === today;
      const isPast = date < today;
      const isFuture = date > today;
      const weight = sortedWeights.find((w) => w.date === date)?.weight_kg ?? null;
      const prior = sortedWeights.filter((w) => w.date < date);
      const weightPrev = prior.length ? prior[prior.length - 1]!.weight_kg : null;

      const meals = {} as Record<FoodSlot, LedgerMeal>;
      let mealsLogged = 0;
      for (const slot of FOOD_SLOTS) {
        const anchor = mealPlan.find((m) => m.slot === slot)!;
        const log = resolveMealLog(slot, daily?.meals_eaten?.[slot], mealPlan);
        if (log) mealsLogged += 1;
        const state = mealCellState(log != null, date, anchor, today, nowMin);
        meals[slot] = {
          slot,
          anchor,
          log,
          items: itemsByKey.get(`${date}:${slot}`),
          planned: selections.get(`${date}:${slot}`) ?? null,
          state,
        };
      }

      const { todayPlan, todayWorkout } = deriveTodayPlanAndWorkout(
        workouts,
        date,
        prof.weekly_commitments ?? null,
      );
      const type = todayPlan?.type;
      const presc = todayWorkout?.coach_prescription ?? null;
      const rest = isRestType(type);
      const done = todayWorkout?.status === 'completed';
      const rawRunTarget = runTargets.get(date) ?? null;
      const runTarget = isRunType(type) ? rawRunTarget : null;
      // Coach note: the prescription's reason, else the run target's
      // description, else a pending coach suggestion targeting this date.
      const coachNote =
        presc?.reason?.trim() ||
        runTarget?.description?.trim() ||
        suggestions
          .map((s) => s.suggestion as { date?: string; reason?: string } | null)
          .find((s) => s?.date === date && s.reason)?.reason?.trim() ||
        null;
      const session: LedgerSession = {
        plan: todayPlan,
        workout: todayWorkout,
        type,
        source: todayWorkout?.source,
        label: (todayWorkout?.source === 'coach' && presc?.name) || todayPlan?.plan || '—',
        done,
        rest,
        missing: isPast && !rest && !done && type != null,
        fit: fitByDate.get(date) ?? null,
        coachNote: presc?.reason?.trim() || null,
        runTarget,
      };

      const gaps: LedgerRow[] = [];
      if (isPast && weight == null) gaps.push('weight');
      for (const slot of FOOD_SLOTS) if (meals[slot].state === 'missing') gaps.push(slot);
      if (session.missing) gaps.push('session');

      return {
        date,
        isToday,
        isPast,
        isFuture,
        weight,
        weightPrev,
        daily,
        target,
        mealPlan,
        meals,
        totals: {
          cal: daily?.calories ?? 0,
          p: daily?.protein_g ?? 0,
          f: daily?.fat_g ?? 0,
          c: daily?.carb_g ?? 0,
        },
        mealsLogged,
        session,
        coachNote,
        gaps,
      };
    });

    const gaps: Gap[] = days.flatMap((d) =>
      d.gaps.map((row) => ({ date: d.date, row: row as Gap['row'] })),
    );

    return {
      weekStart,
      dates,
      days,
      byDate: new Map(days.map((d) => [d.date, d])),
      gaps,
      profile: prof,
      weights: sortedWeights,
    };
  })();

  return {
    ...week,
    today,
    nowMin,
    loading: profileQuery.isLoading || weightsQuery.isLoading || dailyLoading || workoutLoading,
    error: firstError,
  };
}
